import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Sparkles, PlayCircle, Mic2, Sun, Bell, CheckCircle2, Loader2, Compass } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InspirationComingSoonPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your first name and email so we know who to write to.");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        name: name.trim(),
        email: email.trim(),
        source: "inspiration",
      });
      setSubscribed(true);
      toast.success("You're on the list!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40 backdrop-blur-lg bg-opacity-95">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="inspiration-back-btn"
              onClick={() => navigate("/")}
              aria-label="Back to home"
              className="text-text-secondary hover:text-primary transition-colors p-1"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="inspiration-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center"
              aria-label="Explore care types"
            >
              <img src="/tyvena-lockup-horizontal.png" alt="Tyvena" className="h-9 md:h-10 w-auto" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-20">
        <div
          data-testid="inspiration-coming-soon"
          className="bg-white rounded-3xl border-2 border-secondary/25 p-10 md:p-14 text-center shadow-sm"
        >
          <div className="inline-flex w-20 h-20 rounded-full bg-secondary/10 items-center justify-center mb-6">
            <Sparkles className="w-10 h-10 text-secondary" strokeWidth={1.5} />
          </div>

          <p className="text-xs md:text-sm font-body font-bold uppercase tracking-[0.2em] text-secondary mb-3">
            Coming soon
          </p>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold text-accent leading-[1.1] mb-5">
            Inspiration &amp; Education, on your schedule.
          </h1>
          <p className="font-body text-text-secondary text-lg leading-relaxed max-w-xl mx-auto">
            A quiet corner for the mind and heart. Daily and weekly videos, our podcast episodes, and short lessons — designed to lift you up and teach you something useful, right when you have a spare few minutes.
          </p>

          {/* What's coming — three simple cards */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="bg-secondary/5 border border-secondary/20 rounded-2xl p-5 flex flex-col items-center gap-2">
              <PlayCircle className="w-7 h-7 text-secondary" />
              <span className="font-body font-semibold text-sm text-text-primary">Daily &amp; Weekly Videos</span>
              <span className="font-body text-xs text-text-secondary leading-snug">Bite-sized encouragement and how-to&apos;s.</span>
            </div>
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex flex-col items-center gap-2">
              <Mic2 className="w-7 h-7 text-primary" />
              <span className="font-body font-semibold text-sm text-text-primary">The Tyvena Podcast</span>
              <span className="font-body text-xs text-text-secondary leading-snug">Real conversations from the care journey.</span>
            </div>
            <div className="bg-accent/5 border border-accent/20 rounded-2xl p-5 flex flex-col items-center gap-2">
              <Sun className="w-7 h-7 text-accent" />
              <span className="font-body font-semibold text-sm text-text-primary">Daily Inspiration</span>
              <span className="font-body text-xs text-text-secondary leading-snug">A gentle nudge, wherever you are.</span>
            </div>
          </div>

          {/* CTA back to what IS available */}
          <div className="mt-10">
            <p className="font-body text-sm text-text-secondary mb-4 italic">
              While you wait, Asher is already here whenever you need him.
            </p>
            <button
              data-testid="inspiration-cta-asher"
              onClick={() => navigate("/chat")}
              className="group inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white rounded-full font-body font-bold px-6 py-3 transition-all shadow-md hover:shadow-lg"
            >
              <Compass className="w-4 h-4 group-hover:scale-110 transition-transform" />
              Chat with Asher
            </button>
          </div>

          {/* Waitlist */}
          <div className="mt-10 pt-6 border-t border-[#E2E8F0]">
            {subscribed ? (
              <div
                data-testid="inspiration-subscribed-confirmation"
                className="inline-flex items-center gap-2 text-secondary font-body text-base font-semibold"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span>You&apos;re on the list, {name.split(" ")[0]} — we&apos;ll send the first episode the day it drops.</span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-secondary" />
                  <p className="font-body font-semibold text-text-primary text-sm">
                    Want the first video &amp; podcast the moment they launch?
                  </p>
                </div>
                <form
                  data-testid="inspiration-newsletter-form"
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto"
                >
                  <Input
                    data-testid="inspiration-newsletter-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First name"
                    className="flex-1"
                    aria-label="First name"
                  />
                  <Input
                    data-testid="inspiration-newsletter-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-[2]"
                    aria-label="Email"
                  />
                  <Button
                    data-testid="inspiration-newsletter-submit"
                    type="submit"
                    disabled={subscribing}
                    className="bg-secondary text-white hover:bg-secondary/90 rounded-full font-semibold px-6 whitespace-nowrap"
                  >
                    {subscribing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding you…</>
                    ) : (
                      "Notify Me"
                    )}
                  </Button>
                </form>
                <p className="font-body text-xs text-text-secondary mt-3 italic">
                  We&apos;ll only email you when new content drops — no other list, no forwarding, unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>

        <p className="text-center mt-8 font-body text-xs text-text-secondary italic">
          Tyvena. Helping you navigate what&apos;s next.
        </p>
      </main>
    </div>
  );
}
